import React, { ChangeEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Camera, CheckCircle2, FolderPlus, Image, Plus, Sparkles, Trash2 } from 'lucide-react'
import { Folder, NewAlbum, NewFolder } from '../../../../Data/album.dto'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import {
  deleteAlbumAPI,
  getAllRedeemUserAPI,
  updateAlbumAPI,
} from '../../../../Redux/ApiCalls/Dashboard/AlbumAPI'
import { useNavigate } from 'react-router-dom'
import {
  clearError,
  clearFlagAlbums,
  setAlbumLoading,
} from '../../../../Redux/Slice/Dashboard/AlbumSlice'
import LoadingDots from '../../../atoms/Utlis/LoadinDots'
import { createFolderAPI, getFoldersForAlbum } from '../../../../Redux/ApiCalls/Dashboard/FolderApi'
import FolderCard from '../../../atoms/Dashboard/SingleAlbumPage/FolderCard'
import AddFolderModal from '../../../atoms/Dashboard/SingleAlbumPage/CreateFolderModal'
import { showErrorToast } from '../../../atoms/Utlis/Toast'
import BrandDatePicker from '../../../atoms/Utlis/BrandDatePicker'
import imageCompression from 'browser-image-compression'
import Errortext from '../../../atoms/Utlis/Errortext'
import DeletePopup from '../../../atoms/Dashboard/Folder/DeletePopup'
import MarketplaceEmptyState from '../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState'
import { IMAGE_CDN } from '../../../../config/config'
import PermissionGate from '../../../atoms/Utlis/PermissionGate'
import { usePermissions } from '../../../../hooks/usePermissions'
import {
  FormStack,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  DetailsGrid,
  FieldsRow,
  Field,
  Label,
  Input,
  RequiredStar,
  CoverUploadArea,
  CoverDropzone,
  CoverHint,
  CoverSubhint,
  CoverPreviewWrap,
  CoverPreview,
  RemoveCoverBtn,
  FolderSectionHeader,
  FolderCount,
  AddFolderBtn,
  FolderGrid,
  HeroActions,
  HeroSaveBtn,
  DeleteBtn,
} from './albumFormSharedStyles'

interface EditAlbumPageProps {
  heroActionsRef: React.RefObject<HTMLDivElement>
}

const EditAlbumPage: React.FC<EditAlbumPageProps> = ({ heroActionsRef }) => {
  const [album, setAlbum] = useState<NewAlbum>({
    name: '',
    date: '',
    media_type: 1,
    image: '',
  })
  const [customerName, setCustomerName] = useState<string>('')
  const [customerMobile, setCustomerMobile] = useState<string>('')
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [slectedImage, setSelectedImage] = useState<File | null>(null)
  const [showError, setShowError] = useState<boolean>(false)
  const [activeButton, setActiveButton] = useState(true)
  const [folders, setFolders] = useState<Folder[] | []>([])
  const dispatch = useAppDispatch()
  const [isCompressing, setIsCompressing] = useState<boolean>(false)
  const [deleteAlbumPopup, setDeleteAlbumPopup] = useState<boolean>(false)
  const isDeletingAlbum = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [currentFolder, setCurrentFolder] = useState<NewFolder | null>(null)
  const {
    isUpdate,
    loading,
    currentAlbum,
    folderLoading,
    updatedFolderList,
  } = useAppSelector((state) => state.album)
  const [createFolderModal, setCreateFolderModal] = useState(false)
  const [heroPortalReady, setHeroPortalReady] = useState(false)
  const { hasPermission } = usePermissions()
  const canCreateFolder = hasPermission('albums.create_folder')
  const canRedeemUsers = hasPermission('albums.redeem_code_users')
  const initializedAlbumId = useRef<number | null>(null)
  const navigate = useNavigate()

  useLayoutEffect(() => {
    setHeroPortalReady(true)
  }, [])

  useEffect(() => {
    if (isUpdate && !isDeletingAlbum.current) navigate(-1)
  }, [isUpdate, navigate])

  useEffect(() => {
    setFolders([])
    if (currentAlbum?.folders) setFolders(currentAlbum.folders)
  }, [currentAlbum?.folders])

  useEffect(() => {
    if (!currentAlbum) {
      navigate('/dashboard/')
      return
    }

    if (initializedAlbumId.current === currentAlbum.id) return
    initializedAlbumId.current = currentAlbum.id

    if (canRedeemUsers) {
      dispatch(getAllRedeemUserAPI({ album_id: currentAlbum.id }))
    }
    dispatch(getFoldersForAlbum(currentAlbum.id))
    setAlbum((prev) => ({
      ...prev,
      name: currentAlbum.name,
      date: currentAlbum.date,
      image: currentAlbum.image,
    }))
    setCustomerName(currentAlbum.customer_name || '')
    setCustomerMobile(currentAlbum.customer_mobile || '')
    setActiveButton(true)
    setImagePreview(`${IMAGE_CDN}/${currentAlbum.image}`)
  }, [currentAlbum, dispatch, navigate])

  useEffect(() => {
    if (!currentAlbum?.id) return
    const hasPendingFolderUpdate = updatedFolderList.some(
      (folder) => folder.project_id === currentAlbum.id
    )
    if (hasPendingFolderUpdate) {
      dispatch(getFoldersForAlbum(currentAlbum.id))
    }
  }, [updatedFolderList, currentAlbum?.id, dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearError())
      dispatch(clearFlagAlbums())
    }
  }, [dispatch])

  const isValidPhoneNumber = (phone: string): boolean =>
    /^[6-9]\d{9}$/.test(phone)

  const isValidCustomerName = (name: string): boolean =>
    /^[a-zA-Z\s.'-]{2,50}$/.test(name.trim())

  const isAlbumChange = () => {
    if (!currentAlbum) return false
    return (
      album.name !== currentAlbum.name ||
      album.date !== currentAlbum.date ||
      album.image !== currentAlbum.image ||
      slectedImage !== null ||
      customerName !== (currentAlbum.customer_name || '') ||
      customerMobile !== (currentAlbum.customer_mobile || '')
    )
  }

  useEffect(() => {
    if (currentAlbum && isAlbumChange()) {
      setActiveButton(false)
    } else {
      setActiveButton(true)
    }
  }, [
    currentAlbum,
    slectedImage,
    album.name,
    album.image,
    album.date,
    customerName,
    customerMobile,
  ])

  const validData = () => {
    const isClientNameValid =
      customerName.trim().length === 0 || isValidCustomerName(customerName)
    const isClientMobileValid =
      customerName.trim().length > 0
        ? customerMobile.length === 10 && isValidPhoneNumber(customerMobile)
        : customerMobile.length === 0 ||
          (customerMobile.length === 10 && isValidPhoneNumber(customerMobile))

    return (
      album.name.length > 0 &&
      album.date.length > 0 &&
      (album.image.length > 0 || slectedImage) &&
      isClientNameValid &&
      isClientMobileValid
    )
  }

  const canSaveChanges = !activeButton && isAlbumChange()

  const handleSubmit = useCallback(async () => {
    if (!validData()) {
      setShowError(true)
    } else if (!isAlbumChange()) {
      return
    } else if (currentAlbum) {
      const formData = new FormData()
      dispatch(setAlbumLoading())
      if (slectedImage) formData.append('image', slectedImage)
      formData.append('project_id', `${currentAlbum.id}`)
      formData.append('name', album.name)
      formData.append('date', album.date)
      if (customerName.trim().length > 0) {
        formData.append('customer_name', customerName.trim())
      }
      if (customerMobile.length > 0) {
        formData.append('customer_mobile', customerMobile)
      }
      try {
        await dispatch(updateAlbumAPI(formData)).unwrap()
        navigate('/albums/all')
      } catch {
        // handled by reducer
      }
    }
  }, [
    album,
    currentAlbum,
    customerMobile,
    customerName,
    dispatch,
    navigate,
    slectedImage,
  ])

  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      let file = event.target.files[0]
      if (file.size / 1024 / 1024 > 2) {
        setIsCompressing(true)
        const compressedBlob = await compressImage(file)
        file = blobToFile(compressedBlob, file.name)
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = () => {
          setSelectedImage(file)
          setIsCompressing(false)
          setImagePreview(reader.result as string)
        }
      } else {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = () => {
          setSelectedImage(file)
          setImagePreview(reader.result as string)
        }
      }
    }
  }

  const blobToFile = (blob: Blob, fileName: string): File =>
    new File([blob], fileName, { type: blob.type })

  const compressImage = async (file: File): Promise<Blob> => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: false,
    }
    try {
      return await imageCompression(file, options)
    } catch (error) {
      console.error('Error compressing image:', error)
      throw error
    }
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'customerName') {
      setCustomerName(value)
    } else if (name === 'customerMobile') {
      const digitsOnly = value.replace(/\D/g, '')
      if (digitsOnly.length <= 10) setCustomerMobile(digitsOnly)
    } else {
      setAlbum((prev) => {
        if (name === 'date' || (name === 'name' && value.length < 25)) {
          return { ...prev, [name]: value }
        }
        return prev
      })
    }
  }

  const handleDivClick = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    fileInputRef.current?.click()
  }

  const handleFolderCreation = (folder: NewFolder) => {
    if (folder.name.trim().length === 0) {
      showErrorToast('Please enter folder name')
    } else {
      const formData = new FormData()
      formData.append('project_id', `${currentAlbum?.id}`)
      formData.append('name', folder.name)
      folder.images.forEach((image) => {
        formData.append('files', image.image)
        formData.append('media_types', '1')
      })
      dispatch(createFolderAPI(formData))
    }
  }

  const removeImageandPreview = () => {
    setImagePreview(null)
    setAlbum((prev) => ({ ...prev, image: '' }))
    setSelectedImage(null)
  }

  const handleDeleteAlbum = async (id: number) => {
    setDeleteAlbumPopup(false)
    isDeletingAlbum.current = true
    try {
      await dispatch(deleteAlbumAPI({ album_id: id })).unwrap()
      navigate('/dashboard/albums/all', { replace: true })
    } catch {
      isDeletingAlbum.current = false
      // handled by reducer
    }
  }

  if (!currentAlbum) return null

  const heroActionsNode = (
    <HeroActions>
      <PermissionGate permission="albums.delete">
        <DeleteBtn type="button" onClick={() => setDeleteAlbumPopup(true)}>
          <Trash2 size={14} />
          Delete
        </DeleteBtn>
      </PermissionGate>
      {loading ? (
        <LoadingDots />
      ) : (
        <PermissionGate permission="albums.update">
          <HeroSaveBtn type="button" $ready={canSaveChanges} onClick={handleSubmit}>
            {canSaveChanges && <CheckCircle2 size={16} />}
            Save changes
          </HeroSaveBtn>
        </PermissionGate>
      )}
    </HeroActions>
  )

  return (
    <>
      {heroPortalReady &&
        heroActionsRef.current &&
        createPortal(heroActionsNode, heroActionsRef.current)}

      {createPortal(
        deleteAlbumPopup ? (
          <DeletePopup
            cancel={() => setDeleteAlbumPopup(false)}
            Delete={() => handleDeleteAlbum(currentAlbum.id)}
            text="Are you sure you want to delete entire album"
            buttonText="Delete"
          />
        ) : null,
        document.body
      )}

      <FormStack>
        <FormCard>
          <CardHeader>
            <CardIcon>
              <Image size={18} />
            </CardIcon>
            <div>
              <CardTitle>Album details</CardTitle>
              <CardSubtitle>Update cover photo and album information</CardSubtitle>
            </div>
          </CardHeader>

          <DetailsGrid>
            <CoverUploadArea>
              <Label>Cover photo<RequiredStar>*</RequiredStar></Label>
              {imagePreview ? (
                <CoverPreviewWrap>
                  <CoverPreview
                    src={imagePreview}
                    alt="Album cover"
                    onClick={handleDivClick}
                  />
                  <RemoveCoverBtn
                    type="button"
                    onClick={removeImageandPreview}
                    aria-label="Remove cover photo"
                  >
                    ×
                  </RemoveCoverBtn>
                </CoverPreviewWrap>
              ) : (
                <CoverDropzone onClick={handleDivClick}>
                  <Camera size={28} color="#c94daa" />
                  <CoverHint>
                    {isCompressing ? 'Uploading…' : 'Add cover photo'}
                  </CoverHint>
                  <CoverSubhint>JPG or PNG · Recommended: 1920px wide</CoverSubhint>
                </CoverDropzone>
              )}
              <Errortext
                show={showError && album.image.length === 0 && !slectedImage}
                message="Please upload image."
              />
              <input
                type="file"
                accept=".jpg, .jpeg, .png"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
            </CoverUploadArea>

            <div>
              <FieldsRow>
                <Field>
                  <Label>Name<RequiredStar>*</RequiredStar></Label>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Album name"
                    onChange={onChange}
                    value={album.name}
                  />
                  <Errortext
                    show={showError && album.name.length === 0}
                    message="Please provide a valid album name."
                  />
                </Field>
                <Field>
                  <Label>Creation date<RequiredStar>*</RequiredStar></Label>
                  <BrandDatePicker
                    name="date"
                    value={album.date}
                    required
                    onChange={(v) => setAlbum((prev) => ({ ...prev, date: v }))}
                  />
                  <Errortext
                    show={showError && album.date.length === 0}
                    message="Please provide a valid album date."
                  />
                </Field>
              </FieldsRow>

              <FieldsRow>
                <Field>
                  <Label>Client name</Label>
                  <Input
                    type="text"
                    name="customerName"
                    placeholder="Enter client name"
                    onChange={onChange}
                    value={customerName}
                  />
                  <Errortext
                    show={
                      showError &&
                      customerName.trim().length > 0 &&
                      !isValidCustomerName(customerName)
                    }
                    message="Please enter a valid client name (2-50 characters, letters only)"
                  />
                </Field>
                <Field>
                  <Label>
                    Client phone
                    {customerName.trim().length > 0 && <RequiredStar>*</RequiredStar>}
                  </Label>
                  <Input
                    type="tel"
                    name="customerMobile"
                    placeholder="10-digit phone number"
                    onChange={onChange}
                    value={customerMobile}
                    maxLength={10}
                  />
                  <Errortext
                    show={
                      showError &&
                      customerName.trim().length > 0 &&
                      customerMobile.length === 0
                    }
                    message="Client phone number is compulsory when client name is entered."
                  />
                  <Errortext
                    show={
                      showError &&
                      customerMobile.length > 0 &&
                      (customerMobile.length < 10 || !isValidPhoneNumber(customerMobile))
                    }
                    message={
                      customerMobile.length < 10
                        ? 'Phone number must be 10 digits'
                        : 'Please enter a valid 10-digit phone number starting with 6-9'
                    }
                  />
                </Field>
              </FieldsRow>
            </div>
          </DetailsGrid>
        </FormCard>

        <FormCard>
          <FolderSectionHeader>
            <CardHeader style={{ margin: 0, flex: 1 }}>
              <CardIcon>
                <FolderPlus size={18} />
              </CardIcon>
              <div>
                <CardTitle>Folders</CardTitle>
                <CardSubtitle>
                  Manage folders in this album
                  {folders.length > 0 && (
                    <FolderCount> · {folders.length}</FolderCount>
                  )}
                </CardSubtitle>
              </div>
            </CardHeader>
            <PermissionGate permission="albums.create_folder">
              <AddFolderBtn type="button" onClick={() => setCreateFolderModal(true)}>
                <FolderPlus size={15} />
                Add folder
              </AddFolderBtn>
            </PermissionGate>
          </FolderSectionHeader>

          <AddFolderModal
            totalFoldersLength={folders.length}
            isOpen={createFolderModal}
            onRequestClose={() => setCreateFolderModal(false)}
            currentFolder={null}
            imageLimit={20}
            setCurrentFolder={setCurrentFolder}
            onSubmit={handleFolderCreation}
          />

          {folderLoading ? (
            <LoadingDots />
          ) : folders.length > 0 ? (
            <FolderGrid>
              {folders.map((folder: Folder) => (
                <FolderCard key={folder.id} folder={folder} isNew={false} />
              ))}
            </FolderGrid>
          ) : (
            <MarketplaceEmptyState
              padding="36px 20px"
              icon={<FolderPlus size={34} strokeWidth={1.5} />}
              title="No folders yet"
              description="Create folders to organize photos in this album — Engagement, Reception, and more."
              actions={
                canCreateFolder
                  ? [
                      {
                        label: 'Add folder',
                        onClick: () => setCreateFolderModal(true),
                        icon: <Plus size={15} />,
                      },
                    ]
                  : []
              }
              hint={{
                icon: <Sparkles size={13} />,
                text: 'Upload photos into each folder after creating it',
              }}
            />
          )}
        </FormCard>
      </FormStack>
    </>
  )
}

export default EditAlbumPage
